import { property, remote, supertypeClass } from '@haventech/amorphic';

import { ApplicationTypes, ClientCustomerProps, CPMetadataFilingTypes } from '../../js/interfaces/CoverpathTypes';
import { AMCAuth0ResponseUser, Auth0Client } from '../../../common/js/authentication/Auth0Client';
import { Auth0Utils } from '../../../common/js/authentication/Auth0Utils';
import { AppConfigTypes } from '../../../common/js/config/AppConfig';
import { Customer } from '../../../common/js/customer/Customer';
import { Policy } from '../../../common/js/customer/Policy';
import { BaseQuote } from '../../../common/js/quote/BaseQuote';
import { MMDirectQuote } from '../quote/mm-direct/MMDirectQuote';
import { QuoteMap } from '../quote/QuoteClassMap';
import { Product } from '../../../common/js/static/Product';
import { MMDirectPolicy } from './MMDirectPolicy';
import { CoverpathUtils } from '../../js/static/CoverpathUtils';
import { Utils } from '../../../common/js/Utils';
import { CasApplication } from '../coverpath_subclasses/CasApplication';
import { CoverpathDocuSign } from '../../../coverpath-common/js/docusign/CoverpathDocuSign';
import { MMDirectApplication } from './MMDirectApplication';
import { LoggerSingleton, Category } from '../../../common/js/logger/LoggerSingleton';
import { UICategory, UILoggerSingleton } from '../../../common/js/logger/UILoggerSingleton';

@supertypeClass({ toClient: true, toServer: true })
export class MMDirectCustomer extends Customer {
  @property({
    toServer: false,
    getType: () => MMDirectApplication,
    comment: 'All the applications for a given customer',
  })
  protected applications: Array<MMDirectApplication> = [];

  @property({ toServer: false })
  channel: string = 'MMDirect';

  @property()
  applicationSectionIndex: number;

  @property({
    type: Array,
    getType: () => BaseQuote,
    comment: 'insurance quotes for a customer. one customer to many quotes',
  })
  insuranceQuotes: Array<BaseQuote> = [];

  get mmdQuote() {
    const mmdQuote = this.insuranceQuotes.find((quote) => {
      return !quote.isPrior && quote.amorphicGetClassName() === MMDirectQuote.amorphicClassName;
    });
    return mmdQuote ? (mmdQuote as MMDirectQuote) : null;
  }

  // these three quotes for coverpath api compatability
  get currentQuote(): BaseQuote {
    return this.mmdQuote;
  }

  get primaryQuote(): BaseQuote {
    return this.mmdQuote;
  }

  get clientQuote(): BaseQuote {
    return this.mmdQuote;
  }

  @property()
  quote: string;

  constructor() {
    super();
  }

  static new(quoteType?: string) {
    if (quoteType) {
      const customer = new MMDirectCustomer().init();
      customer.initializeQuote(quoteType);
      return customer;
    } else {
      return new MMDirectCustomer().init();
    }
  }

  static getDynamicQuoteObj(customer: Customer, type: string): BaseQuote {
    return QuoteMap[type].new(customer);
  }

  init() {
    super.init();

    this.channel = Product.channels.MMDirect.code;
    this.percentFilled = 0;
    return this;
  }

  get firstNameLastName(): string {
    const firstNameLastName: string = `${this.firstName ? this.firstName : ''}${
      this.lastName ? ' ' + this.lastName : ''
    }`;
    return firstNameLastName.trim();
  }

  @remote({ on: 'server' })
  createPolicy(quoteKey): Promise<void> {
    if (this.application && this.application.primaryPolicy && this.application.primaryPolicy.isSubmitting()) {
      throw new Error('Application in Progress - Cannot start a new one');
    }

    const createApp = () => {
      if (!this.application) {
        this.application = MMDirectApplication.new(this);
        return this.application.assignApplicationNumber();
      }
    };

    const createPol = () => {
      if (!this.application.primaryPolicy) {
        const policy = this.newPolicy();
        policy.product = quoteKey;
        this.application.addPrimaryPolicy(policy);
      }

      (this.application.primaryPolicy as MMDirectPolicy).updateWithQuoteInfo(this.mmdQuote);
    };

    return Promise.resolve().then(createApp).then(createPol);
  }

  newPolicy() {
    const policy = MMDirectPolicy.new(this);

    policy.waiver = null;

    return policy;
  }

  @remote({ on: 'server' })
  async submitPolicy() {
    // Make sure this is not a duplicate submission
    const policy = this.application.primaryPolicy;

    if (policy.submittedAt && policy.policyNumber) {
      return true;
    }

    const isEmailValidated = await this.isEmailValidated();

    if (!isEmailValidated) {
      throw new Error('Internal Error - email not validated');
    }
    try {
      const policyNumber = await policy.assignPolicyNumber();
    } catch (err) {
      LoggerSingleton.getInstance().error(
        {
          module: 'Customer.submitPolicy',
          function: 'createWorkflow',
          category: Category.request,
          error: err,
        },
        `An error occured generating the policyNumber`
      );
      return err;
    }

    return this.startPolicyWorkflow();
  }

  initializeQuote(quoteType: string): void {
    if (!this.insuranceQuotes || this.insuranceQuotes.length === 0) {
      const quote = MMDirectCustomer.getDynamicQuoteObj(this, quoteType);
      quote.isAdvisorQuote = false;
      this.insuranceQuotes.push(quote);
    }
  }

  static create(props: ClientCustomerProps) {
    const setPolicyProps = () => {
      customer.application.primaryPolicy.ownerPersonResidentialAddress.state = props.address.state;
      return customer;
    };

    const customer = MMDirectCustomer.new(props.quoteType);
    customer.reset();
    customer.updateProps(props);

    return customer.createPolicy(Product.products.MMDirectTerm.quoteKey.quoteKey).then(setPolicyProps);
  }

  updateProps(props: ClientCustomerProps) {
    this.email = props.person.email;
    this.firstName = props.person.firstName;
    this.lastName = props.person.lastName;
    this.advanceApplicationProgress(props.progress || 10);
    this.primaryCustomer.person.firstName = this.firstName;
    this.primaryCustomer.person.lastName = this.lastName;
    this.primaryCustomer.address.state = props.address.state;
    this.primaryCustomer.person.age = props.person.age;
    if (
      !this.application ||
      !this.application.primaryPolicy ||
      (this.application.primaryPolicy && !this.application.primaryPolicy.insured)
    ) {
      this.primaryCustomer.person.email = this.email;
    }

    if (!!props.primaryQuote) {
      props.primaryQuote.customer = this;
      if (this.mmdQuote.hasBeenInitialized()) {
        this.insuranceQuotes.unshift(props.primaryQuote);
      } else {
        this.insuranceQuotes = [props.primaryQuote];
      }
    }
    return this;
  }

  retrieveQuoteSpec() {
    return {
      insuranceQuotes: true,
    };
  }

  fetchQuotes() {
    const idMap = {};

    idMap[this._id] = this;

    return this.fetch(this.retrieveQuoteSpec(), null, idMap);
  }

  // @TODO - MMDirect guys please double check this. You were using it without it being defined
  isApplicationInProgress() {
    return this.progress === 12;
  }

  protected async isEmailValidated(): Promise<boolean> {
    // Legacy validation
    let isEmailValidated = this.emailValidated;

    if (!isEmailValidated) {
      const isAuth0Enabled = await Auth0Utils.isAuth0EnabledByProvider(AppConfigTypes.AUTH0_ENABLED_MMD);

      if (isAuth0Enabled) {
        const accountInfo: AMCAuth0ResponseUser = await Auth0Client.getAMCUserInfo(this.email);
        isEmailValidated = accountInfo.email_verified;
      }
    }

    return isEmailValidated;
  }

  public resetApplication(sourceApplication: MMDirectApplication, newProgress, newPolicy?: MMDirectPolicy) {
    this.priorApplications.push(sourceApplication);

    if (typeof newProgress !== 'undefined') {
      this.resetApplicationProgress(newProgress);
    }

    return this.switchPolicy(newPolicy);
  }

  @remote({ on: 'server' })
  async switchPolicy(policyToSwitchTo?: Policy): Promise<void> {
    const originalPolicy: Policy = this.application.primaryPolicy;

    const primaryAddress = this.primaryCustomer.address;
    const product = this.application.primaryPolicy.product;

    return Promise.resolve().then(markPriorApp.bind(this)).then(createApp.bind(this)).then(switchPol.bind(this));

    function markPriorApp() {
      // use a direct reference of the application so persistSave is called on the application itself.
      // if using this.application, only an active application will be returned and it will be undefined in this case
      // because the same application was just marked as prior.
      const application = this.application;
      application.type = ApplicationTypes.PRIOR;
      return application.persistSave();
    }

    async function createApp() {
      this.application = MMDirectApplication.new(this);
      await this.application.assignApplicationNumber();
      return this.application.persistSave();
    }

    function switchPol() {
      if (this.application) {
        this.reset();

        this.primaryCustomer.address = primaryAddress;
        this.policies = [];
        if (policyToSwitchTo) {
          this.application.addPrimaryPolicy(policyToSwitchTo);
        } else {
          const policy = this.newPolicy();
          this.application.addPrimaryPolicy(policy);
        }
        this.updateInsuredEmailIfNeeded();
      }
      this.application.primaryPolicy.updateWithPolicyInfo(originalPolicy);
    }
  }

  /**
   * to determine whether 2019 filing is enabled on Coverpath
   * when there is no application it does not matter - such as on client log out
   * this function exists when we cannot access the configs
   * can be removed once 2019 filing is released in all states
   */
  isCoverpath2019MetadataEnabled(policy?: Policy): boolean {
    if (!this.application && !policy) {
      return false;
    }

    const application = policy ? policy.application : this.application;
    const currentPolicy = application.primaryPolicy;
    const metadataVersion = currentPolicy.appMetadataVersion;
    const deprecatedMetadata2018Version = currentPolicy.isICCApplication()
      ? CoverpathUtils.deprecatedICCMetadata2018Version
      : CoverpathUtils.deprecatedNonICCMetadata2018Version;
    const channelCode = currentPolicy.getChannelCode();
    if (Utils.isCoverpathPlatform(channelCode) && metadataVersion > deprecatedMetadata2018Version) {
      return true;
    } else {
      return false;
    }
  }

  getCoverpathFilingType(policy?: Policy): string {
    const application = policy ? policy.application : this.application;
    const currentPolicy = application.primaryPolicy;
    const metadataVersion = currentPolicy.appMetadataVersion;
    const isCAApplication = (currentPolicy as MMDirectPolicy).isCAApplication();

    if (metadataVersion > CoverpathUtils.deprecatedMetadata2020Version && !isCAApplication) {
      return CPMetadataFilingTypes.CP2021;
    } else if (metadataVersion > CoverpathUtils.deprecatedMetadata2019Version) {
      return CPMetadataFilingTypes.CP2020;
    } else if (this.isCoverpath2019MetadataEnabled(currentPolicy)) {
      return CPMetadataFilingTypes.CP2019;
    } else {
      return CPMetadataFilingTypes.CP2018;
    }
  }

  /**
   * Get the active applications for this customer.
   * An active application does not have its type populated with any values.
   * A non-active application could have its type populated as 'prior' or 'deleted'
   */
  get activeApplications(): Array<MMDirectApplication> {
    return this.applications;
  }

  get application(): MMDirectApplication {
    return this.getApplication();
  }

  set application(application: MMDirectApplication) {
    this.setApplication(application);
  }

  getApplication(): MMDirectApplication {
    if (this._currentApplication) {
      return this._currentApplication as MMDirectApplication;
    } else if (this.activeApplications.length === 1) {
      return this.activeApplications[0] as MMDirectApplication;
    } else if (this.activeApplications.length > 1) {
      const error = {
        isHumanRelated: false,
        data: { customerId: this._id },
      };
      UILoggerSingleton.getInstance().error({
        module: 'Customer',
        function: 'get application',
        category: UICategory.request,
        error: error,
      });
      return null;
      //may be redirect the customer to dashboard view
    }
  }

  setApplication(application: MMDirectApplication) {
    this._currentApplication = application;
    if (!this.applications.includes(application as MMDirectApplication)) {
      this.applications.push(application as MMDirectApplication);
      this.applications = this.applications.filter((app) => app.type === ApplicationTypes.ACTIVE);
    }
  }

  getDocuSign() {
    return CoverpathDocuSign;
  }
}
